import { useMemo, useRef, useState } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Badge,
  Box,
  Button,
  Callout,
  Card,
  Checkbox,
  Code,
  Flex,
  Heading,
  IconButton,
  RadioGroup,
  Separator,
  Table,
  Tabs,
  Text,
  TextField,
} from "@radix-ui/themes";
import { Dialog, Grid } from "@radix-ui/themes";
import { Bell, Check, Database, Info, Pencil, Plus, Trash2, Upload } from "lucide-react";
import toast from "react-hot-toast";
import PageTemplate from "@/components/page-template/PageTemplate";
import SearchableSelect from "@/components/ui/SearchableSelect";
import withTenantAdminRequired from "@/hoc/withTenantAdminRequired";
import {
  DeviceSourceConfig,
  PushProviderStatus,
  tenantPushService,
} from "@/services/breeze/TenantPushService";
import { anyService } from "@/services/breeze/AnyService";
import { entityService } from "@/services/breeze/EntityService";
import { useAuth } from "@/providers/AuthProvider";
import { useUsersWithDevices } from "@/hooks/useUsersWithDevices";
import { Field } from "@/types/breeze/Field";
import ImagePickerField from "@/components/ui/ImagePickerField";
import PushButtonsField, { PushButtonRow } from "@/components/ui/PushButtonsField";
import IosButtonGroupPicker from "@/components/ui/IosButtonGroupPicker";
import HelpPopover from "@/components/ui/HelpPopover";
import { PushTemplate } from "@/services/breeze/TenantPushService";
import {
  IOS_GROUP_PRESETS,
  PUSH_TEMPLATE_PRESETS,
  PushTemplatePreset,
} from "@/components/push/pushPresets";
import PushSendHistory from "@/components/push/PushSendHistory";
import PushTemplateEditorDialog from "@/components/push/PushTemplateEditorDialog";
import { messageFromError } from "@/utils/text";

type TabKey = "providers" | "devices" | "templates" | "categories" | "test" | "history";

export default withTenantAdminRequired(function PushNotificationsPage() {
  const [tab, setTab] = useState<TabKey>("providers");
  const [templatesGalleryOpen, setTemplatesGalleryOpen] = useState(false);
  const [categoriesAddMode, setCategoriesAddMode] = useState<"preset" | "custom" | null>(null);

  const { data: categoriesData } = useQuery({
    queryKey: ["push-categories"],
    queryFn: () => tenantPushService.getCategories(),
    enabled: tab === "categories",
  });
  const categoriesHasAny = (categoriesData?.categories?.length ?? 0) > 0;

  let rightContent: React.ReactNode = null;
  if (tab === "templates") {
    rightContent = (
      <Button onClick={() => setTemplatesGalleryOpen(true)}>
        <Plus size={14} /> Add template
      </Button>
    );
  } else if (tab === "categories" && categoriesHasAny) {
    rightContent = (
      <Button onClick={() => setCategoriesAddMode((m) => (m ? null : "preset"))}>
        <Plus size={14} /> {categoriesAddMode ? "Close" : "Add button group"}
      </Button>
    );
  }

  return (
    <PageTemplate
      title="Push Notifications"
      description="Connect a delivery provider, register devices, and send notifications from your workflows."
      rightContent={rightContent}
    >
      <Box mx="auto">
        <Tabs.Root value={tab} onValueChange={(v) => setTab(v as TabKey)} mb="4">
          <Tabs.List>
            <Tabs.Trigger value="providers">Providers</Tabs.Trigger>
            <Tabs.Trigger value="devices">Registered devices</Tabs.Trigger>
            <Tabs.Trigger value="templates">Templates</Tabs.Trigger>
            <Tabs.Trigger value="categories">iOS button groups</Tabs.Trigger>
            <Tabs.Trigger value="test">Send notification</Tabs.Trigger>
            <Tabs.Trigger value="history">History</Tabs.Trigger>
          </Tabs.List>
        </Tabs.Root>

        {tab === "providers" && <ProvidersTab />}
        {tab === "devices" && <DevicesTab />}
        {tab === "templates" && (
          <TemplatesTab
            galleryOpen={templatesGalleryOpen}
            setGalleryOpen={setTemplatesGalleryOpen}
          />
        )}
        {tab === "categories" && (
          <CategoriesTab
            addMode={categoriesAddMode}
            setAddMode={setCategoriesAddMode}
          />
        )}
        {tab === "test" && <TestSendTab />}
        {tab === "history" && <HistoryTab />}
      </Box>
    </PageTemplate>
  );
});

function ProvidersTab() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["push-credentials"],
    queryFn: () => tenantPushService.getCredentials(),
  });

  const fcm = data?.providers.find((p) => p.provider === "fcm");

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["push-credentials"] });

  return (
    <Flex direction="column" gap="4">
      {isLoading ? (
        <Text size="2" color="gray">Loading…</Text>
      ) : (
        <FcmProviderCard status={fcm} onChange={invalidate} />
      )}
    </Flex>
  );
}

function FcmProviderCard({
  status,
  onChange,
}: {
  status?: PushProviderStatus;
  onChange: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewJson, setPreviewJson] = useState<string>("");

  const saveMutation = useMutation({
    mutationFn: (json: string) => tenantPushService.saveFcm(json),
    onSuccess: (res) => {
      toast.success(`FCM connected for project ${res.project_id}.`);
      setPreviewJson("");
      onChange();
    },
    onError: (e: unknown) => toast.error(messageFromError(e, "Could not save FCM credentials")),
  });

  const deleteMutation = useMutation({
    mutationFn: () => tenantPushService.deleteCredential("fcm"),
    onSuccess: () => {
      toast.success("FCM credentials removed.");
      onChange();
    },
    onError: (e: unknown) => toast.error(messageFromError(e, "Could not remove FCM credentials")),
  });

  const projectId = (status?.metadata?.project_id as string) || null;
  const clientEmail = (status?.metadata?.client_email as string) || null;

  return (
    <Card style={{ contain: "none" }}>
      <Flex align="center" justify="between" mb="3">
        <Flex direction="column">
          <Flex align="center" gap="3">
            <Heading size="3">Firebase Cloud Messaging</Heading>
            {status?.is_configured ? (
              <Badge color="green" variant="soft">
                <Check size={12} /> Configured
              </Badge>
            ) : (
              <Badge color="gray" variant="soft">Not configured</Badge>
            )}
          </Flex>
          <Text size="1" color="gray">
            iOS via Firebase iOS SDK + Android. One service-account JSON per tenant.
          </Text>
        </Flex>
        {status?.is_configured && (
          <Button
            variant="soft"
            color="red"
            onClick={() => {
              if (window.confirm("Remove FCM credentials? Devices registered with FCM will stop receiving notifications.")) {
                deleteMutation.mutate();
              }
            }}
            disabled={deleteMutation.isPending}
          >
            <Trash2 size={14} /> Remove
          </Button>
        )}
      </Flex>

      {status?.is_configured && (
        <Box mb="3" p="2" style={{
          border: "1px solid var(--gray-5)",
          borderRadius: "var(--radius-2)",
          background: "var(--gray-2)",
        }}>
          <Text size="1" color="gray">Project</Text>
          <Code size="1" variant="ghost" style={{ display: "block" }}>{projectId}</Code>
          <Text size="1" color="gray" mt="1" style={{ display: "block" }}>Service account</Text>
          <Code size="1" variant="ghost" style={{ display: "block", wordBreak: "break-all" }}>
            {clientEmail}
          </Code>
        </Box>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".json,application/json"
        style={{ display: "none" }}
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          const text = await file.text();
          setPreviewJson(text);
          if (fileInputRef.current) fileInputRef.current.value = "";
        }}
      />
      <Flex gap="2" align="center">
        <Button
          variant="soft"
          onClick={() => fileInputRef.current?.click()}
          disabled={saveMutation.isPending}
        >
          <Upload size={14} /> {status?.is_configured ? "Replace" : "Upload"} service-account JSON
        </Button>
        {previewJson && (
          <Button
            onClick={() => saveMutation.mutate(previewJson)}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? "Saving…" : "Save"}
          </Button>
        )}
      </Flex>
      <Text size="1" color="gray" mt="2" style={{ display: "block" }}>
        Generate one in <a href="https://console.firebase.google.com/" target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent-11)" }}>Firebase Console</a> → Project settings → Service accounts → <strong>Generate new private key</strong>.
      </Text>

      {status?.is_configured && (
        <>
          <Separator my="4" size="4" />
          <DeviceSourceSection provider="fcm" status={status} onChange={onChange} />
        </>
      )}
    </Card>
  );
}

function DeviceSourceSection({
  provider,
  status,
  onChange,
}: {
  provider: "fcm" | "web_push";
  status: PushProviderStatus;
  onChange: () => void;
}) {
  const existing = (status.metadata?.device_source as DeviceSourceConfig | undefined) ?? undefined;
  const [kind, setKind] = useState<"tenant_devices" | "entity_field">(
    existing?.kind ?? "tenant_devices",
  );
  const [entity, setEntity] = useState(existing?.entity ?? "");
  const [tokenField, setTokenField] = useState(existing?.token_field ?? "");
  const [ownerUserField, setOwnerUserField] = useState(existing?.owner_user_field ?? "");
  const [ownerEntityField, setOwnerEntityField] = useState(existing?.owner_entity_field ?? "");
  const [ownerSelf, setOwnerSelf] = useState<boolean>(existing?.owner_self ?? false);

  const { data: entities, isLoading: entitiesLoading } = useQuery({
    queryKey: ["entities", "all"],
    queryFn: () => entityService.getAll(),
  });

  const usersEntityId = useMemo(
    () => entities?.find((e) => e.name === "anythink_users")?.id,
    [entities],
  );
  const selected = useMemo(
    () => entities?.find((e) => e.name === entity),
    [entities, entity],
  );

  const isTextish = (f: Field) =>
    f.database_type === "text" || f.database_type === "varchar";
  const isUserFk = (f: Field) =>
    f.database_type === "user" ||
    (f.relationship?.target_entity_id === usersEntityId &&
      f.relationship?.type === "many-to-one");
  const isEntityFk = (f: Field) => f.relationship?.type === "many-to-one";

  const tokenFieldOptions = useMemo(
    () =>
      (selected?.fields ?? [])
        .filter(isTextish)
        .map((f) => ({ value: f.name, label: f.label ? `${f.name} — ${f.label}` : f.name })),
    [selected],
  );
  const ownerUserFieldOptions = useMemo(
    () =>
      (selected?.fields ?? [])
        .filter(isUserFk)
        .map((f) => ({ value: f.name, label: f.label ? `${f.name} — ${f.label}` : f.name })),
    [selected, usersEntityId],
  );
  const ownerEntityFieldOptions = useMemo(
    () =>
      (selected?.fields ?? [])
        .filter(isEntityFk)
        .filter((f) => !isUserFk(f))
        .map((f) => ({
          value: f.name,
          label: f.label ? `${f.name} — ${f.label}` : f.name,
        })),
    [selected, usersEntityId],
  );

  const entityOptions = useMemo(
    () =>
      (entities ?? [])
        .filter((e) => !e.name.startsWith("anythink_"))
        .map((e) => ({ value: e.name, label: e.name })),
    [entities],
  );

  const onEntityChange = (next: string) => {
    setEntity(next);
    setTokenField("");
    setOwnerUserField("");
    setOwnerEntityField("");
    setOwnerSelf(false);
  };

  const saveMutation = useMutation({
    mutationFn: (cfg: DeviceSourceConfig) => tenantPushService.saveDeviceSource(provider, cfg),
    onSuccess: () => {
      toast.success("Device source updated.");
      onChange();
    },
    onError: (e: unknown) => toast.error(messageFromError(e, "Could not save device source")),
  });

  const onSave = () => {
    if (kind === "entity_field") {
      if (!entity || !tokenField) {
        toast.error("Entity and token field are required.");
        return;
      }
    }
    saveMutation.mutate({
      kind,
      entity: entity || undefined,
      token_field: tokenField || undefined,
      owner_user_field: ownerUserField || undefined,
      owner_entity_field: ownerEntityField || undefined,
      owner_self: ownerSelf,
    });
  };

  return (
    <Box>
      <Flex align="center" gap="2" mb="2">
        <Database size={14} />
        <Heading size="2">Device source</Heading>
      </Flex>
      <Text size="1" color="gray" mb="3" style={{ display: "block" }}>
        Where tokens for this provider live. Default is the Anythink-managed device
        registry. Choose <strong>From an entity field</strong> when your apps already
        write device tokens to a column on an existing entity (e.g. <Code size="1" variant="ghost">profiles.fcm_token</Code>).
      </Text>

      <RadioGroup.Root
        value={kind}
        onValueChange={(v) => setKind(v as "tenant_devices" | "entity_field")}
        mb="3"
      >
        <Flex direction="column" gap="2">
          <RadioGroup.Item value="tenant_devices">
            <Text size="2">
              <strong>Anythink-managed</strong> — devices register via <Code size="1" variant="ghost">POST /push/devices</Code>.
            </Text>
          </RadioGroup.Item>
          <RadioGroup.Item value="entity_field">
            <Text size="2">
              <strong>From an entity field</strong> — read tokens from a column on a tenant entity.
            </Text>
          </RadioGroup.Item>
        </Flex>
      </RadioGroup.Root>

      {kind === "entity_field" && (
        <Flex direction="column" gap="3" mb="3">
          <Box>
            <Text as="label" size="1" weight="medium" mb="1" style={{ display: "block" }}>
              Entity
            </Text>
            <SearchableSelect
              value={entity}
              onChange={onEntityChange}
              options={entityOptions}
              placeholder={entitiesLoading ? "Loading entities…" : "Pick an entity"}
              searchPlaceholder="Search entities…"
            />
          </Box>
          <Box>
            <Text as="label" size="1" weight="medium" mb="1" style={{ display: "block" }}>
              Token column
            </Text>
            <SearchableSelect
              value={tokenField}
              onChange={setTokenField}
              options={tokenFieldOptions}
              placeholder={!selected ? "Pick an entity first" : "Pick a text column"}
              searchPlaceholder="Search columns…"
              disabled={!selected}
            />
          </Box>
          <Box>
            <Text as="label" size="1" weight="medium" mb="1" style={{ display: "block" }}>
              Owner-user field
            </Text>
            <SearchableSelect
              value={ownerUserField}
              onChange={setOwnerUserField}
              options={ownerUserFieldOptions}
              placeholder={!selected ? "Pick an entity first" : "Pick the FK to anythink_users"}
              searchPlaceholder="Search columns…"
              disabled={!selected}
            />
            <Text size="1" color="gray" mt="1" style={{ display: "block" }}>
              Required for <Code size="1" variant="ghost">audience.kind=user / users</Code>.
            </Text>
          </Box>
          <Box>
            <Text as="label" size="1" weight="medium" mb="1" style={{ display: "block" }}>
              Owner-entity field
            </Text>
            <SearchableSelect
              value={ownerEntityField}
              onChange={setOwnerEntityField}
              options={ownerEntityFieldOptions}
              placeholder={!selected ? "Pick an entity first" : "Pick a many-to-one FK (CRM owner)"}
              searchPlaceholder="Search columns…"
              disabled={!selected || ownerSelf}
            />
            <Text size="1" color="gray" mt="1" style={{ display: "block" }}>
              For CRM-style entity audiences. Leave blank when the row IS the owner.
            </Text>
          </Box>
          <Flex align="center" gap="2" mt="1">
            <Checkbox
              checked={ownerSelf}
              onCheckedChange={(v) => {
                setOwnerSelf(v === true);
                if (v === true) setOwnerEntityField("");
              }}
            />
            <Text size="1">
              The entity row <strong>is</strong> the owner (e.g. <Code size="1" variant="ghost">contacts.fcm_token</Code> belongs to that contact).
            </Text>
          </Flex>
        </Flex>
      )}

      {kind === "entity_field" && entity && tokenField && ownerUserField && !ownerSelf && (
        <Box mb="3">
          <CurrentUserTokenPreview
            entity={entity}
            tokenField={tokenField}
            ownerUserField={ownerUserField}
          />
        </Box>
      )}

      <Button onClick={onSave} disabled={saveMutation.isPending}>
        {saveMutation.isPending ? "Saving…" : "Save device source"}
      </Button>
    </Box>
  );
}

function CurrentUserTokenPreview({
  entity,
  tokenField,
  ownerUserField,
}: {
  entity: string;
  tokenField: string;
  ownerUserField: string;
}) {
  const { currentUser } = useAuth();
  const userId = currentUser?.id;

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["device-source-preview", entity, tokenField, ownerUserField, userId],
    queryFn: () =>
      anyService.getAll<Record<string, unknown>>(
        entity,
        [tokenField],
        `${ownerUserField}=${userId}`,
        1,
        5,
      ),
    enabled: Boolean(userId),
  });

  if (!userId) return null;

  const rows = data?.items ?? [];
  const rawToken = rows.length > 0 ? (rows[0]?.[tokenField] as string | null | undefined) : undefined;
  const hasToken = typeof rawToken === "string" && rawToken.length > 0;

  const preview = hasToken
    ? rawToken!.length > 16
      ? `${rawToken!.slice(0, 8)}…${rawToken!.slice(-4)}`
      : rawToken!
    : null;

  const refresh = (
    <Button
      size="1"
      variant="ghost"
      onClick={() => refetch()}
      disabled={isFetching}
      type="button"
    >
      {isFetching ? "Checking…" : "Refresh"}
    </Button>
  );

  let body: React.ReactNode;
  if (isLoading) {
    body = <Text size="1" color="gray">Checking <Code size="1" variant="ghost">{entity}.{tokenField}</Code>…</Text>;
  } else if (isError) {
    body = (
      <Text size="1" color="red">
        Couldn't query <Code size="1" variant="ghost">{entity}</Code>. Check that the
        entity exists and your role has <Code size="1" variant="ghost">{entity}:read</Code>.
      </Text>
    );
  } else if (rows.length === 0) {
    body = (
      <Text size="1" color="gray">
        No <Code size="1" variant="ghost">{entity}</Code> row found where{" "}
        <Code size="1" variant="ghost">{ownerUserField}={userId}</Code>. Your app needs
        to create a row for the current user before tokens can be read.
      </Text>
    );
  } else if (!hasToken) {
    body = (
      <Text size="1" color="orange">
        Row found, but <Code size="1" variant="ghost">{tokenField}</Code> is empty.
        Sign out and back in on the device, or relaunch the app, so it re-registers
        its push token.
      </Text>
    );
  } else {
    body = (
      <Flex align="center" gap="2">
        <Check size={12} color="var(--green-11)" />
        <Text size="1" color="green">
          Live token for your user:
        </Text>
        <Code size="1" variant="ghost">{preview}</Code>
      </Flex>
    );
  }

  return (
    <Box
      p="2"
      style={{
        border: "1px solid var(--gray-5)",
        borderRadius: "var(--radius-2)",
        background: "var(--gray-2)",
      }}
    >
      <Flex justify="between" align="center" mb="1">
        <Text size="1" weight="medium">Token preview (your user)</Text>
        {refresh}
      </Flex>
      {body}
    </Box>
  );
}

type UnifiedDevice = {
  rowKey: string;
  provider: "fcm" | "web_push" | "apns";
  source: "tenant_devices" | "entity_field";
  token_preview: string;
  user_id: number | null;
  owner_entity_name: string | null;
  owner_entity_id: number | null;
  last_seen_at: string;
  unregistered_at: string | null;
  // Only set on tenant_devices rows — entity_field rows can't be removed from here.
  managed_id?: number;
};

function tokenPreview(token: string | null | undefined): string {
  if (!token) return "—";
  if (token.length <= 16) return token;
  return `${token.slice(0, 8)}…${token.slice(-4)}`;
}

function TemplatesTab({
  galleryOpen,
  setGalleryOpen,
}: {
  galleryOpen: boolean;
  setGalleryOpen: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["push-templates"],
    queryFn: () => tenantPushService.getTemplates(),
  });
  const [editing, setEditing] = useState<PushTemplate | { preset: PushTemplatePreset } | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: number) => tenantPushService.deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["push-templates"] });
      toast.success("Template removed.");
    },
    onError: (e: unknown) => toast.error(messageFromError(e, "Could not remove template")),
  });

  const templates = data?.templates ?? [];

  return (
    <Flex direction="column" gap="4">
      <Box>
        <Heading size="3">Templates</Heading>
        <Text size="2" color="gray">
          {isLoading ? "Loading…" : `${templates.length} configured`}
        </Text>
        {templates.length > 0 && <Separator size="4" mt="3" />}
      </Box>

      {!isLoading && templates.length === 0 ? (
        <Card style={{ contain: "none" }}>
          <Heading size="3" mb="1">Start from a pattern</Heading>
          <Text size="2" color="gray" mb="3" style={{ display: "block" }}>
            Pre-built shapes for the most common push notifications. Pick one to start from — you can edit anything before saving.
          </Text>
          <PresetGalleryGrid
            onPick={(preset) => {
              setEditing({ preset });
              setGalleryOpen(false);
            }}
          />
        </Card>
      ) : (
        <Table.Root variant="surface" size="1">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Template type</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Title</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Category</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Buttons</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Active</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell></Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {templates.map((t) => (
              <Table.Row key={t.id}>
                <Table.Cell><Code size="1" variant="ghost">{t.template_type}</Code></Table.Cell>
                <Table.Cell><Text size="1">{t.title}</Text></Table.Cell>
                <Table.Cell>
                  {t.category ? <Code size="1" variant="ghost">{t.category}</Code> : <Text size="1" color="gray">—</Text>}
                </Table.Cell>
                <Table.Cell>
                  {(t.buttons?.length ?? 0) > 0 ? (
                    <Text size="1">{t.buttons!.length} button{t.buttons!.length === 1 ? "" : "s"}</Text>
                  ) : <Text size="1" color="gray">—</Text>}
                </Table.Cell>
                <Table.Cell>
                  <Badge size="1" variant="soft" color={t.is_active ? "green" : "gray"}>
                    {t.is_active ? "On" : "Off"}
                  </Badge>
                </Table.Cell>
                <Table.Cell style={{ width: 1, whiteSpace: "nowrap" }}>
                  <Flex gap="1">
                    <Button size="1" variant="soft" onClick={() => setEditing(t)}>Edit</Button>
                    <IconButton
                      size="1"
                      variant="soft"
                      color="red"
                      onClick={() => {
                        if (window.confirm(`Remove template '${t.template_type}'? Workflows referencing it will fail at run time.`)) {
                          deleteMutation.mutate(t.id);
                        }
                      }}
                      title="Remove template"
                    >
                      <Trash2 size={12} />
                    </IconButton>
                  </Flex>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}

      {galleryOpen && (
        <PresetGalleryDialog
          onClose={() => setGalleryOpen(false)}
          onPick={(preset) => {
            setEditing({ preset });
            setGalleryOpen(false);
          }}
        />
      )}

      {editing && (
        <PushTemplateEditorDialog
          initial={"id" in editing ? editing : null}
          presetInitial={"preset" in editing ? editing.preset : null}
          onClose={() => setEditing(null)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ["push-templates"] });
            setEditing(null);
          }}
        />
      )}
    </Flex>
  );
}

function IosGroupPresetGrid({ existing }: { existing: string[] }) {
  const queryClient = useQueryClient();
  const existingSet = new Set(existing);
  const addMutation = useMutation({
    mutationFn: async (preset: (typeof IOS_GROUP_PRESETS)[number]) => {
      await tenantPushService.upsertCategory({
        category: preset.category,
        label: preset.label,
        description: preset.description,
        is_enabled: true,
      });
      await tenantPushService.syncCategories({
        app_version: "admin-preset",
        categories: [
          {
            id: preset.category,
            actions: preset.actions.map((a) => ({
              id: a.id,
              label: a.label,
              destructive: a.destructive,
              opens_app: a.opens_app,
            })),
          },
        ],
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["push-categories"] });
      toast.success("Pattern added.");
    },
    onError: (e: unknown) => toast.error(messageFromError(e, "Could not add pattern")),
  });
  return (
    <Grid columns={{ initial: "1", sm: "2", lg: "3" }} gap="4">
      {IOS_GROUP_PRESETS.map((preset) => {
        const already = existingSet.has(preset.category);
        return (
          <Box
            key={preset.category}
            style={{
              border: `1px solid var(--${already ? "green-a6" : "gray-a5"})`,
              borderRadius: "var(--radius-4)",
              background: already ? "var(--green-a2)" : "var(--color-panel-solid)",
            }}
          >
            <Box p="4">
              <Flex direction="column" gap="3" style={{ height: "100%" }}>
                <Flex justify="between" align="start" gap="2">
                  <Box>
                    <Heading size="3" weight="medium">{preset.label}</Heading>
                    <Box mt="1">
                      <Code size="1" variant="ghost">category = "{preset.category}"</Code>
                    </Box>
                  </Box>
                  {already ? (
                    <Badge size="1" variant="soft" color="green">
                      <Check size={10} /> Added
                    </Badge>
                  ) : (
                    <Button
                      size="2"
                      onClick={() => addMutation.mutate(preset)}
                      disabled={addMutation.isPending}
                    >
                      <Plus size={12} /> Add
                    </Button>
                  )}
                </Flex>
                <Text size="2" color="gray">{preset.description}</Text>
                <Box mt="auto">
                  <Text size="1" color="gray" mb="1" style={{ display: "block" }}>Actions:</Text>
                  <Flex gap="1" wrap="wrap">
                    {preset.actions.map((a) => (
                      <Badge key={a.id} size="1" variant="soft" color={a.destructive ? "red" : "blue"}>
                        {a.label}
                      </Badge>
                    ))}
                  </Flex>
                </Box>
              </Flex>
            </Box>
          </Box>
        );
      })}
    </Grid>
  );
}

function PresetGalleryGrid({ onPick }: { onPick: (preset: PushTemplatePreset) => void }) {
  return (
    <Grid columns={{ initial: "1", sm: "2", md: "3", lg: "4" }} gap="4">
      {PUSH_TEMPLATE_PRESETS.map((preset) => (
        <Card
          key={preset.id}
          onClick={() => onPick(preset)}
          style={{ cursor: "pointer", contain: "none" }}
        >
          <Box p="4">
            <Flex direction="column" gap="3" style={{ height: "100%" }}>
              <Box
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: "var(--radius-3)",
                  background: "var(--gray-3)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 22,
                  lineHeight: 1,
                }}
                aria-hidden
              >
                {preset.icon}
              </Box>
              <Box>
                <Heading size="3" weight="medium">{preset.name}</Heading>
                <Text size="1" color="gray" mt="1" style={{ display: "block" }}>{preset.description}</Text>
              </Box>
              {preset.template.buttons && preset.template.buttons.length > 0 && (
                <Box mt="auto">
                  <Text size="1" color="gray" mb="1" style={{ display: "block" }}>Buttons:</Text>
                  <Flex gap="1" wrap="wrap">
                    {preset.template.buttons.map((b) => (
                      <Badge key={b.id} size="1" variant="soft">{b.label}</Badge>
                    ))}
                  </Flex>
                </Box>
              )}
            </Flex>
          </Box>
        </Card>
      ))}
    </Grid>
  );
}

function PresetGalleryDialog({
  onClose,
  onPick,
}: {
  onClose: () => void;
  onPick: (preset: PushTemplatePreset) => void;
}) {
  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Content style={{ maxWidth: 900, contain: "none" }}>
        <Dialog.Title>Start from a pattern</Dialog.Title>
        <Dialog.Description size="2" mb="4">
          Pre-built shapes for the most common push notifications. Pick one — you can edit anything before saving.
        </Dialog.Description>
        <PresetGalleryGrid onPick={onPick} />
        <Flex justify="end" mt="4">
          <Dialog.Close>
            <Button variant="soft" color="gray">Cancel</Button>
          </Dialog.Close>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}


function CategoriesTab({
  addMode,
  setAddMode,
}: {
  addMode: "preset" | "custom" | null;
  setAddMode: React.Dispatch<React.SetStateAction<"preset" | "custom" | null>>;
}) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["push-categories"],
    queryFn: () => tenantPushService.getCategories(),
  });
  const [drafts, setDrafts] = useState<Record<number, GroupDraft>>({});
  const [newCategory, setNewCategory] = useState({ category: "", label: "", description: "", is_enabled: true });

  const upsertMutation = useMutation({
    mutationFn: (input: { category: string; label?: string; description?: string; is_enabled: boolean; ios_labels?: Record<string, string> }) =>
      tenantPushService.upsertCategory(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["push-categories"] });
      setNewCategory({ category: "", label: "", description: "", is_enabled: true });
      toast.success("Button group saved.");
    },
    onError: (e: unknown) => toast.error(messageFromError(e, "Could not save button group")),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => tenantPushService.deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["push-categories"] });
      toast.success("Button group removed.");
    },
    onError: (e: unknown) => toast.error(messageFromError(e, "Could not remove button group")),
  });

  const renameMutation = useMutation({
    mutationFn: (input: { id: number; newCategory: string }) =>
      tenantPushService.renameCategory(input.id, input.newCategory),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["push-categories"] });
      queryClient.invalidateQueries({ queryKey: ["push-templates"] });
      queryClient.invalidateQueries({ queryKey: ["push-category-references"] });
      const cascaded = result.templates_cascaded;
      const dangling = result.dangling.workflow_triggers.length + result.dangling.workflow_steps.length;
      if (dangling > 0) {
        toast(
          `Renamed. Updated ${cascaded} template${cascaded === 1 ? "" : "s"} automatically. ${dangling} workflow reference${dangling === 1 ? "" : "s"} still point at the old id — see the badge on the card.`,
          { duration: 8000 },
        );
      } else {
        toast.success(
          cascaded > 0
            ? `Renamed. Updated ${cascaded} template${cascaded === 1 ? "" : "s"} automatically.`
            : "Category id renamed.",
        );
      }
    },
    onError: (e: unknown) => toast.error(messageFromError(e, "Could not rename category")),
  });

  const categories = data?.categories ?? [];
  const hasAny = categories.length > 0;

  return (
    <Flex direction="column" gap="5">

      {/* ── 1. CONFIGURED GROUPS ──────────────────────────────────────── */}
      <Box>
        <Heading size="3">Your button groups</Heading>
        <Text size="2" color="gray">
          {isLoading
            ? "Loading…"
            : hasAny
              ? `${categories.length} configured`
              : "None yet — pick a pattern below to get started."}
        </Text>

        {hasAny && <Separator size="4" my="3" />}

        {hasAny && (
          <ConfiguredGroupsGrid
            categories={categories}
            drafts={drafts}
            setDrafts={setDrafts}
            upsertMutation={upsertMutation}
            renameMutation={renameMutation}
            deleteMutation={deleteMutation}
          />
        )}
      </Box>

      {/* ── 2. ADD OPTIONS ────────────────────────────────────────────── */}
      {(addMode || !hasAny) && (
        <Box>
          <Flex gap="2" mb="3">
            <Button
              variant={addMode === "preset" || !hasAny ? "solid" : "soft"}
              onClick={() => setAddMode("preset")}
            >
              Pick a pattern
            </Button>
            <Button
              variant={addMode === "custom" ? "solid" : "soft"}
              onClick={() => setAddMode("custom")}
            >
              Custom group
            </Button>
          </Flex>

          {(addMode === "preset" || (!hasAny && addMode !== "custom")) && (
            <Card style={{ contain: "none" }}>
              <Heading size="3" mb="1">Common patterns</Heading>
              <Text size="2" color="gray" mb="3" style={{ display: "block" }}>
                Tap a pattern to add it. The button labels are what the app should register; your Flutter developer adds the matching <Code size="1" variant="ghost">UNNotificationCategory</Code> with these action ids and pushes start showing buttons.
              </Text>
              <IosGroupPresetGrid existing={categories.map((c) => c.category)} />
            </Card>
          )}

          {addMode === "custom" && (
            <Card style={{ contain: "none" }}>
              <Heading size="3" mb="1">Custom button group</Heading>
              <Text size="2" color="gray" mb="3" style={{ display: "block" }}>
                For a group that isn't in the patterns. The id must match what the Flutter app uses when registering the <Code size="1" variant="ghost">UNNotificationCategory</Code>.
              </Text>
              <Flex direction="column" gap="2">
                <Flex gap="2" wrap="wrap">
                  <Box style={{ flex: "1 1 180px" }}>
                    <Text as="label" size="1" weight="medium" mb="1" style={{ display: "block" }}>Group id</Text>
                    <TextField.Root
                      placeholder="workout_reminder"
                      value={newCategory.category}
                      onChange={(e) => setNewCategory({ ...newCategory, category: e.target.value })}
                    />
                  </Box>
                  <Box style={{ flex: "1 1 180px" }}>
                    <Text as="label" size="1" weight="medium" mb="1" style={{ display: "block" }}>Display name</Text>
                    <TextField.Root
                      placeholder="Workout reminder"
                      value={newCategory.label}
                      onChange={(e) => setNewCategory({ ...newCategory, label: e.target.value })}
                    />
                  </Box>
                  <Box style={{ flex: "2 1 240px" }}>
                    <Text as="label" size="1" weight="medium" mb="1" style={{ display: "block" }}>What it shows</Text>
                    <TextField.Root
                      placeholder="Buttons: Start now / Snooze 15 min / Skip"
                      value={newCategory.description}
                      onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                    />
                  </Box>
                </Flex>
                <Flex justify="between" align="center">
                  <Flex align="center" gap="2">
                    <Checkbox
                      checked={newCategory.is_enabled}
                      onCheckedChange={(v) => setNewCategory({ ...newCategory, is_enabled: v === true })}
                    />
                    <Text size="2">Enabled (sends referencing this group will be accepted)</Text>
                  </Flex>
                  <Button
                    onClick={() => upsertMutation.mutate({
                      category: newCategory.category.trim(),
                      label: newCategory.label.trim() || undefined,
                      description: newCategory.description.trim() || undefined,
                      is_enabled: newCategory.is_enabled,
                    })}
                    disabled={upsertMutation.isPending || !newCategory.category.trim()}
                  >
                    {upsertMutation.isPending ? "Saving…" : "Add group"}
                  </Button>
                </Flex>
              </Flex>
            </Card>
          )}
        </Box>
      )}
    </Flex>
  );
}

type GroupDraft = {
  label?: string;
  description?: string;
  is_enabled?: boolean;
  category?: string;
  ios_labels?: Record<string, string>;
};

function CategoryUsageBadge({ references }: { references?: {
  templates: Array<{ id: number }>;
  workflow_triggers: Array<{ workflow_id: number }>;
  workflow_steps: Array<{ workflow_id: number; step_key: string | null }>;
} }) {
  if (!references) return null;
  const tpl = references.templates.length;
  const wfCount = new Set([
    ...references.workflow_triggers.map((t) => t.workflow_id),
    ...references.workflow_steps.map((s) => s.workflow_id),
  ]).size;
  if (tpl === 0 && wfCount === 0) {
    return <Badge size="1" variant="soft" color="gray">Not used yet</Badge>;
  }
  const parts: string[] = [];
  if (tpl > 0) parts.push(`${tpl} template${tpl === 1 ? "" : "s"}`);
  if (wfCount > 0) parts.push(`${wfCount} workflow${wfCount === 1 ? "" : "s"}`);
  return <Badge size="1" variant="soft" color="blue">Used in {parts.join(" · ")}</Badge>;
}

function ConfiguredGroupsGrid({
  categories,
  drafts,
  setDrafts,
  upsertMutation,
  renameMutation,
  deleteMutation,
}: {
  categories: Array<{
    id: number;
    category: string;
    label?: string | null;
    description?: string | null;
    is_enabled: boolean;
    ios_action_order?: string[] | null;
    ios_labels?: Record<string, string> | null;
  }>;
  drafts: Record<number, GroupDraft>;
  setDrafts: (next: Record<number, GroupDraft>) => void;
  upsertMutation: ReturnType<typeof useMutation<unknown, unknown, { category: string; label?: string; description?: string; is_enabled: boolean; ios_labels?: Record<string, string> }>>;
  renameMutation: ReturnType<typeof useMutation<Awaited<ReturnType<typeof tenantPushService.renameCategory>>, unknown, { id: number; newCategory: string }>>;
  deleteMutation: ReturnType<typeof useMutation<unknown, unknown, number>>;
}) {
  const referenceQueries = useQueries({
    queries: categories.map((c) => ({
      queryKey: ["push-category-references", c.id],
      queryFn: () => tenantPushService.getCategoryReferences(c.id),
      staleTime: 30000,
    })),
  });
  const referencesById = new Map(categories.map((c, i) => [c.id, referenceQueries[i].data]));
  return (
    <Grid columns={{ initial: "1", sm: "2", lg: "3" }} gap="4">
      {categories.map((c) => {
        const draft = drafts[c.id];
        const editing = !!draft;
        const actionOrder = c.ios_action_order ?? [];
        const savedLabels = c.ios_labels ?? {};
        const isEnabled = c.is_enabled;

        const enterEdit = () => {
          setDrafts({
            ...drafts,
            [c.id]: {
              label: c.label ?? "",
              description: c.description ?? "",
              is_enabled: c.is_enabled,
              category: c.category,
              ios_labels: { ...savedLabels },
            },
          });
        };
        const cancelEdit = () => {
          const { [c.id]: _, ...rest } = drafts;
          setDrafts(rest);
        };
        const patchDraft = (patch: Partial<GroupDraft>) => {
          setDrafts({ ...drafts, [c.id]: { ...(draft ?? {}), ...patch } });
        };
        const patchLabel = (id: string, value: string) => {
          patchDraft({
            ios_labels: { ...(draft?.ios_labels ?? savedLabels), [id]: value },
          });
        };

        return (
          <Box
            key={c.id}
            style={{
              border: `1px solid var(--${isEnabled ? "gray-a5" : "amber-a6"})`,
              borderRadius: "var(--radius-4)",
              background: isEnabled ? "var(--color-panel-solid)" : "var(--amber-a2)",
              contain: "none",
            }}
          >
            <Box p="4">
              <Flex direction="column" gap="3">
                <Flex justify="between" align="start" gap="2">
                  <Flex direction="column" gap="1" style={{ minWidth: 0 }}>
                    <Code size="1" variant="ghost">{c.category}</Code>
                    <CategoryUsageBadge references={referencesById.get(c.id)} />
                  </Flex>
                  <Flex gap="1" align="center">
                    {!editing && (
                      <>
                        <Flex align="center" gap="1">
                          <Checkbox
                            checked={isEnabled}
                            onCheckedChange={(v) => upsertMutation.mutate({
                              category: c.category,
                              label: c.label ?? undefined,
                              description: c.description ?? undefined,
                              is_enabled: v === true,
                            })}
                          />
                          <Badge size="1" variant="soft" color={isEnabled ? "green" : "gray"}>
                            {isEnabled ? "On" : "Off"}
                          </Badge>
                        </Flex>
                        <IconButton
                          size="1"
                          variant="soft"
                          onClick={enterEdit}
                          title="Edit button group"
                        >
                          <Pencil size={12} />
                        </IconButton>
                        <IconButton
                          size="1"
                          variant="soft"
                          color="red"
                          onClick={() => {
                            if (window.confirm(`Remove button group '${c.category}'?`)) {
                              deleteMutation.mutate(c.id);
                            }
                          }}
                          title="Remove button group"
                        >
                          <Trash2 size={12} />
                        </IconButton>
                      </>
                    )}
                  </Flex>
                </Flex>

                {!editing ? (
                  <>
                    <Box>
                      <Text size="1" color="gray" style={{ display: "block" }}>Display name</Text>
                      <Text size="2" weight="medium">
                        {c.label || <Text color="gray">— not set —</Text>}
                      </Text>
                    </Box>
                    <Box>
                      <Text size="1" color="gray" style={{ display: "block" }}>What it shows</Text>
                      <Text size="2">
                        {c.description || <Text color="gray">— no description —</Text>}
                      </Text>
                    </Box>
                    {actionOrder.length > 0 ? (
                      <Box>
                        <Text size="1" color="gray" mb="1" style={{ display: "block" }}>
                          Buttons ({actionOrder.length})
                        </Text>
                        <Box style={{
                          border: "1px solid var(--gray-a4)",
                          borderRadius: "var(--radius-2)",
                          overflow: "hidden",
                        }}>
                          {actionOrder.map((id, i) => (
                            <Flex
                              key={id}
                              align="center"
                              justify="between"
                              gap="2"
                              px="2"
                              py="2"
                              style={{
                                borderTop: i > 0 ? "1px solid var(--gray-a4)" : "none",
                                background: i % 2 === 0 ? "transparent" : "var(--gray-a2)",
                              }}
                            >
                              <Code size="1" variant="ghost">{id}</Code>
                              <Text size="1" color="gray">→</Text>
                              <Text size="1" weight="medium" style={{ flex: 1, textAlign: "right" }}>
                                {savedLabels[id] ?? id}
                              </Text>
                            </Flex>
                          ))}
                        </Box>
                      </Box>
                    ) : (
                      <Text size="1" color="gray">
                        No buttons synced yet from the iOS app.
                      </Text>
                    )}
                  </>
                ) : (
                  <>
                    <Box>
                      <Flex align="center" gap="1" mb="1">
                        <Text as="label" size="1" weight="medium">Category id</Text>
                        <HelpPopover title="How this id is used">
                          Must match the <Code size="1" variant="ghost">UNNotificationCategory</Code> id your iOS app registers at launch. Changing it affects the send-time payload and any workflow triggers that reference it.
                        </HelpPopover>
                      </Flex>
                      <TextField.Root
                        size="2"
                        value={draft.category ?? c.category}
                        placeholder={c.category}
                        onChange={(e) => patchDraft({ category: e.target.value })}
                      />
                    </Box>
                    <Box>
                      <Text as="label" size="1" weight="medium" mb="1" style={{ display: "block" }}>Display name</Text>
                      <TextField.Root
                        size="2"
                        value={draft.label ?? ""}
                        placeholder={c.category}
                        onChange={(e) => patchDraft({ label: e.target.value })}
                      />
                    </Box>
                    <Box>
                      <Text as="label" size="1" weight="medium" mb="1" style={{ display: "block" }}>What it shows</Text>
                      <TextField.Root
                        size="2"
                        value={draft.description ?? ""}
                        placeholder="Short description of when this is used"
                        onChange={(e) => patchDraft({ description: e.target.value })}
                      />
                    </Box>
                    <Flex align="center" gap="2">
                      <Checkbox
                        checked={draft.is_enabled ?? c.is_enabled}
                        onCheckedChange={(v) => patchDraft({ is_enabled: v === true })}
                      />
                      <Text size="2">Enabled</Text>
                    </Flex>

                    {actionOrder.length > 0 && (
                      <Box>
                        <Flex align="center" gap="1" mb="1">
                          <Text as="label" size="1" weight="medium">
                            Button labels ({actionOrder.length})
                          </Text>
                          <HelpPopover title="Where these labels are used">
                            Defaults for <strong>Android and Web</strong> push buttons. iOS uses whatever the app registered with <Code size="1" variant="ghost">UNNotificationCategory</Code>; edit the app's labels and re-sync to align iOS.
                          </HelpPopover>
                        </Flex>
                        <Box style={{
                          border: "1px solid var(--gray-a4)",
                          borderRadius: "var(--radius-2)",
                          overflow: "hidden",
                        }}>
                          <Flex
                            align="center"
                            gap="2"
                            px="2"
                            py="1"
                            style={{ background: "var(--gray-a3)", borderBottom: "1px solid var(--gray-a4)" }}
                          >
                            <Text size="1" color="gray" style={{ flexShrink: 0, minWidth: 100 }}>
                              Action id (from app)
                            </Text>
                            <Text size="1" color="gray" style={{ width: 16, textAlign: "center" }}>→</Text>
                            <Text size="1" color="gray" style={{ flex: 1 }}>
                              Label (Android · Web)
                            </Text>
                          </Flex>
                          {actionOrder.map((id, i) => (
                            <Flex
                              key={id}
                              align="center"
                              gap="2"
                              px="2"
                              py="2"
                              style={{
                                borderTop: i > 0 ? "1px solid var(--gray-a4)" : "none",
                                background: i % 2 === 0 ? "transparent" : "var(--gray-a2)",
                              }}
                            >
                              <Code size="1" variant="ghost" style={{ flexShrink: 0, minWidth: 100 }}>{id}</Code>
                              <Text size="1" color="gray" style={{ width: 16, textAlign: "center" }}>→</Text>
                              <Box style={{ flex: 1 }}>
                                <TextField.Root
                                  size="1"
                                  value={draft.ios_labels?.[id] ?? savedLabels[id] ?? ""}
                                  placeholder={id}
                                  onChange={(e) => patchLabel(id, e.target.value)}
                                />
                              </Box>
                            </Flex>
                          ))}
                        </Box>
                      </Box>
                    )}

                    <Flex gap="2" justify="end">
                      <Button variant="soft" color="gray" onClick={cancelEdit}>
                        Cancel
                      </Button>
                      <Button
                        onClick={async () => {
                          const nextCategory = (draft.category ?? c.category).trim();
                          const submittedLabels = draft.ios_labels && actionOrder.length > 0
                            ? Object.fromEntries(
                                actionOrder
                                  .map((id) => [id, (draft.ios_labels?.[id] ?? savedLabels[id] ?? "").trim()])
                                  .filter(([, v]) => v),
                              )
                            : undefined;
                          if (nextCategory && nextCategory !== c.category) {
                            try {
                              await renameMutation.mutateAsync({ id: c.id, newCategory: nextCategory });
                            } catch {
                              return;
                            }
                          }
                          upsertMutation.mutate({
                            category: nextCategory || c.category,
                            label: (draft.label ?? "").trim() || undefined,
                            description: (draft.description ?? "").trim() || undefined,
                            is_enabled: draft.is_enabled ?? c.is_enabled,
                            ios_labels: submittedLabels,
                          });
                          cancelEdit();
                        }}
                      >
                        <Check size={12} /> Save changes
                      </Button>
                    </Flex>
                  </>
                )}
              </Flex>
            </Box>
          </Box>
        );
      })}
    </Grid>
  );
}

function DevicesTab() {
  const queryClient = useQueryClient();
  const [includeUnreg, setIncludeUnreg] = useState(false);

  const { data: registry, isLoading: registryLoading } = useQuery({
    queryKey: ["push-devices", includeUnreg],
    queryFn: () => tenantPushService.getDevices(includeUnreg),
  });
  const { data: credentials, isLoading: credsLoading } = useQuery({
    queryKey: ["push-credentials"],
    queryFn: () => tenantPushService.getCredentials(),
  });
  const { userById } = useUsersWithDevices();

  const entityFieldSources = useMemo(
    () =>
      (credentials?.providers ?? [])
        .map((p) => {
          const ds = p.metadata?.device_source as DeviceSourceConfig | undefined;
          if (!ds || ds.kind !== "entity_field" || !ds.entity || !ds.token_field) return null;
          return { provider: p.provider, source: ds };
        })
        .filter(Boolean) as Array<{
          provider: "fcm" | "web_push" | "apns";
          source: DeviceSourceConfig & { entity: string; token_field: string };
        }>,
    [credentials],
  );

  const entityFieldQueries = useQueries({
    queries: entityFieldSources.map(({ provider, source }) => ({
      queryKey: ["push-devices", "entity-field", provider, source.entity, source.token_field],
      queryFn: async () => {
        const fields = [source.token_field, source.owner_user_field, "updated_at"].filter(Boolean) as string[];
        const result = await anyService.getAll<Record<string, unknown>>(source.entity, fields, "", 1, 500);
        return { provider, source, items: result.items };
      },
    })),
  });

  const unified = useMemo<UnifiedDevice[]>(() => {
    const fromRegistry: UnifiedDevice[] = (registry?.devices ?? []).map((d) => ({
      rowKey: `td-${d.id}`,
      provider: d.provider,
      source: "tenant_devices",
      token_preview: d.token_preview,
      user_id: d.user_id,
      owner_entity_name: d.owner_entity_name,
      owner_entity_id: d.owner_entity_id,
      last_seen_at: d.last_seen_at,
      unregistered_at: d.unregistered_at,
      managed_id: d.id,
    }));

    const fromEntities: UnifiedDevice[] = entityFieldQueries.flatMap((q) => {
      if (!q.data) return [] as UnifiedDevice[];
      const { provider, source, items } = q.data;
      return items
        .map((row) => {
          const rawToken = row[source.token_field] as string | null | undefined;
          if (!rawToken) return null;
          const ownerRaw = source.owner_user_field
            ? row[source.owner_user_field]
            : null;
          const ownerId =
            typeof ownerRaw === "object" && ownerRaw !== null && "id" in ownerRaw
              ? (ownerRaw as { id: number }).id
              : typeof ownerRaw === "number"
                ? ownerRaw
                : null;
          const updated = (row.updated_at as string | undefined) ?? new Date(0).toISOString();
          const rowId = (row.id as number | undefined) ?? 0;
          return {
            rowKey: `ef-${provider}-${source.entity}-${rowId}`,
            provider,
            source: "entity_field" as const,
            token_preview: tokenPreview(rawToken),
            user_id: ownerId,
            owner_entity_name: source.entity,
            owner_entity_id: rowId,
            last_seen_at: updated,
            unregistered_at: null,
          };
        })
        .filter(Boolean) as UnifiedDevice[];
    });

    return [...fromRegistry, ...fromEntities].sort(
      (a, b) => new Date(b.last_seen_at).getTime() - new Date(a.last_seen_at).getTime(),
    );
  }, [registry, entityFieldQueries.map((q) => q.dataUpdatedAt).join(",")]);

  const removeMutation = useMutation({
    mutationFn: (id: number) => tenantPushService.removeDevice(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["push-devices"] });
      toast.success("Device removed.");
    },
    onError: (e: unknown) => toast.error(messageFromError(e, "Could not remove device")),
  });

  const anyLoading =
    registryLoading ||
    credsLoading ||
    entityFieldQueries.some((q) => q.isLoading);

  return (
    <Box>
      <Flex justify="between" align="center" mb="3">
        <Box>
          <Heading size="3">Registered devices</Heading>
          <Text size="2" color="gray">
            Every push target your tenant apps have registered, across both the
            Anythink-managed registry and any entity-field device sources.
          </Text>
        </Box>
        <Button
          variant="soft"
          color="gray"
          onClick={() => setIncludeUnreg((v) => !v)}
        >
          {includeUnreg ? "Hide unregistered" : "Show unregistered"}
        </Button>
      </Flex>

      {anyLoading ? (
        <Text size="2" color="gray">Loading…</Text>
      ) : unified.length === 0 ? (
        <Callout.Root color="gray">
          <Callout.Icon><Bell size={16} /></Callout.Icon>
          <Callout.Text>
            No devices yet. Your tenant apps register a device by POSTing to{" "}
            <Code size="1" variant="ghost">/push/devices</Code> — or, if a provider is
            configured with an entity-field device source, by writing a token to that
            entity column.
          </Callout.Text>
        </Callout.Root>
      ) : (
        <Table.Root variant="surface" size="1">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Provider</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Source</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Owner</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Token</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Last seen</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell></Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {unified.map((d) => (
              <UnifiedDeviceRow
                key={d.rowKey}
                device={d}
                ownerUser={d.user_id != null ? userById[d.user_id] : undefined}
                onRemove={() =>
                  d.managed_id != null ? removeMutation.mutate(d.managed_id) : undefined
                }
              />
            ))}
          </Table.Body>
        </Table.Root>
      )}
    </Box>
  );
}

function UnifiedDeviceRow({
  device,
  ownerUser,
  onRemove,
}: {
  device: UnifiedDevice;
  ownerUser?: { id: number; first_name: string; last_name: string; email: string };
  onRemove: () => void;
}) {
  const fullName = ownerUser
    ? [ownerUser.first_name, ownerUser.last_name].filter(Boolean).join(" ").trim()
    : "";
  return (
    <Table.Row>
      <Table.Cell><Badge variant="soft">{prettyProvider(device.provider)}</Badge></Table.Cell>
      <Table.Cell>
        <Badge
          size="1"
          variant="soft"
          color={device.source === "entity_field" ? "purple" : "blue"}
        >
          {device.source === "entity_field" ? "Entity field" : "Registry"}
        </Badge>
      </Table.Cell>
      <Table.Cell>
        {ownerUser ? (
          <Flex direction="column">
            {fullName && <Text size="2">{fullName}</Text>}
            <Text size="1" color="gray">{ownerUser.email}</Text>
          </Flex>
        ) : device.owner_entity_name != null && device.owner_entity_id != null ? (
          <Text size="1">
            <Code size="1" variant="ghost">{device.owner_entity_name} #{device.owner_entity_id}</Code>
          </Text>
        ) : (
          <Text size="1" color="gray">—</Text>
        )}
      </Table.Cell>
      <Table.Cell><Code size="1" variant="ghost">{device.token_preview}</Code></Table.Cell>
      <Table.Cell>
        <Text size="1">{new Date(device.last_seen_at).toLocaleString()}</Text>
      </Table.Cell>
      <Table.Cell>
        {device.unregistered_at
          ? <Badge color="gray" variant="soft">Unregistered</Badge>
          : <Badge color="green" variant="soft">Live</Badge>}
      </Table.Cell>
      <Table.Cell style={{ width: 1 }}>
        {device.managed_id != null ? (
          <IconButton
            size="1"
            variant="soft"
            color="red"
            onClick={() => {
              if (window.confirm("Remove this device?")) onRemove();
            }}
            title="Delete device"
          >
            <Trash2 size={12} />
          </IconButton>
        ) : (
          <Text size="1" color="gray" title="Entity-field rows are owned by the tenant entity; clear the column there to remove.">
            —
          </Text>
        )}
      </Table.Cell>
    </Table.Row>
  );
}

function TestSendTab() {
  const queryClient = useQueryClient();
  const [templateType, setTemplateType] = useState("");
  const [title, setTitle] = useState("Test notification");
  const [body, setBody] = useState("If you can read this, push is working.");
  const [imageUrl, setImageUrl] = useState("");
  const [clickAction, setClickAction] = useState("");
  const [category, setCategory] = useState("");
  const [buttons, setButtons] = useState<PushButtonRow[]>([]);
  const [showOverrides, setShowOverrides] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<PushTemplate | null>(null);
  const [mode, setMode] = useState<"users" | "all">("users");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState("");

  const handleTemplateChange = (next: string) => {
    setTemplateType(next);
    setShowOverrides(false);
    if (next) {
      setTitle(""); setBody(""); setImageUrl("");
      setClickAction(""); setCategory(""); setButtons([]);
    } else {
      setTitle("Test notification");
      setBody("If you can read this, push is working.");
    }
  };

  const { users, isLoading: usersLoading } = useUsersWithDevices();
  const { data: templatesData } = useQuery({
    queryKey: ["push-templates"],
    queryFn: () => tenantPushService.getTemplates(),
  });
  const templates = templatesData?.templates ?? [];
  const usingTemplate = !!templateType;
  const selectedTemplate = templates.find((t) => t.template_type === templateType);

  const { data: categoriesData } = useQuery({
    queryKey: ["push-categories"],
    queryFn: () => tenantPushService.getCategories(),
  });
  const selectedCategoryRow = (categoriesData?.categories ?? []).find((c) => c.category === category);
  const lockedByGroupSendTab = selectedCategoryRow && (selectedCategoryRow.ios_action_order?.length ?? 0) > 0
    ? (selectedCategoryRow.label || selectedCategoryRow.category)
    : null;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.display_name.toLowerCase().includes(q)
        || u.email.toLowerCase().includes(q),
    );
  }, [users, search]);

  const toggle = (id: number) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selectedUsers = useMemo(
    () => users.filter((u) => selectedIds.has(u.id)),
    [users, selectedIds],
  );

  const sendMutation = useMutation({
    mutationFn: () => {
      const ids = Array.from(selectedIds);
      const validButtons = buttons
        .map((b) => ({
          id: (b.id ?? "").trim(),
          label: (b.label ?? "").trim(),
          click_action: (b.click_action ?? "").trim() || undefined,
        }))
        .filter((b) => b.id && b.label);
      return tenantPushService.sendTest({
        template_type: templateType.trim() || undefined,
        title: (title ?? "").trim() || undefined,
        body: (body ?? "").trim() || undefined,
        image_url: (imageUrl ?? "").trim() || undefined,
        click_action: (clickAction ?? "").trim() || undefined,
        category: (category ?? "").trim() || undefined,
        buttons: validButtons.length > 0 ? validButtons : undefined,
        target_user_ids: mode === "users" && ids.length > 0 ? ids : undefined,
      });
    },
    onSuccess: () => {
      const recipientLabel =
        mode === "all"
          ? "all live devices"
          : selectedIds.size === 1
            ? "1 user"
            : `${selectedIds.size} users`;
      toast.success(`Queued push to ${recipientLabel} — see History for delivery.`);
      queryClient.invalidateQueries({ queryKey: ["push-send-logs"] });
    },
    onError: (e: unknown) => toast.error(messageFromError(e, "Could not send")),
  });

  const sendDisabled =
    sendMutation.isPending
    || (!usingTemplate && (!title || !body))
    || (mode === "users" && selectedIds.size === 0);

  return (
    <Card style={{ contain: "none" }}>
      <Heading size="4" mb="1">Send a notification</Heading>
      <Text size="2" color="gray" mb="4" style={{ display: "block" }}>
        Author a push and send to one or more registered users, or broadcast to every live device.
        Same delivery path as a workflow's Send Push Notification step.
      </Text>

      <Flex direction="column" gap="5">
        {/* ── 0. TEMPLATE ─────────────────────────────────────────────── */}
        <Box>
          <SectionHeader title="Template" subtitle="Optional. Pick a template to inherit its fields. Inline values below override individual template fields when set." />
          <Box style={{ maxWidth: 480 }}>
            <SearchableSelect
              value={templateType}
              onChange={handleTemplateChange}
              options={[
                { value: "", label: "— None (use inline content) —" },
                ...templates.filter((t) => t.is_active).map((t) => ({
                  value: t.template_type,
                  label: t.name ? `${t.name} (${t.template_type})` : t.template_type,
                })),
              ]}
              placeholder="Use a template"
              searchPlaceholder="Search templates…"
            />
          </Box>
          {usingTemplate && !selectedTemplate && (
            <Callout.Root color="amber" mt="2">
              <Callout.Icon><Info size={14} /></Callout.Icon>
              <Callout.Text>
                Template <Code size="1" variant="ghost">{templateType}</Code> isn't in your tenant's template list — the send will fail. Pick from the dropdown or create the template under the Templates tab.
              </Callout.Text>
            </Callout.Root>
          )}
        </Box>

        {usingTemplate && selectedTemplate && !showOverrides && (
          <>
            <Separator size="4" />
            <Box>
              <Flex justify="between" align="end" mb="3">
                <SectionHeader title="Preview" subtitle="What will be sent, straight from the template." />
                <Button size="1" variant="soft" onClick={() => setEditingTemplate(selectedTemplate)}>
                  <Pencil size={12} /> Edit template
                </Button>
              </Flex>
              <Card style={{ contain: "none" }}>
                <Flex direction="column" gap="3" p="3">
                  <Box>
                    <Text size="1" color="gray" style={{ display: "block" }}>Title</Text>
                    <Text size="3" weight="medium">{selectedTemplate.title}</Text>
                  </Box>
                  <Box>
                    <Text size="1" color="gray" style={{ display: "block" }}>Body</Text>
                    <Text size="2">{selectedTemplate.body}</Text>
                  </Box>
                  {selectedTemplate.image_url && (
                    <Box>
                      <Text size="1" color="gray" style={{ display: "block" }}>Image</Text>
                      <Code size="1" variant="ghost">{selectedTemplate.image_url}</Code>
                    </Box>
                  )}
                  {selectedTemplate.click_action && (
                    <Box>
                      <Text size="1" color="gray" style={{ display: "block" }}>Deep link when tapped</Text>
                      <Code size="1" variant="ghost">{selectedTemplate.click_action}</Code>
                    </Box>
                  )}
                  {selectedTemplate.category && (
                    <Box>
                      <Text size="1" color="gray" style={{ display: "block" }}>iOS button group</Text>
                      <Code size="1" variant="ghost">{selectedTemplate.category}</Code>
                    </Box>
                  )}
                  {(selectedTemplate.buttons?.length ?? 0) > 0 && (
                    <Box>
                      <Text size="1" color="gray" mb="1" style={{ display: "block" }}>Buttons</Text>
                      <Flex direction="column" gap="1">
                        {selectedTemplate.buttons!.map((b) => (
                          <Flex key={b.id} align="center" gap="2">
                            <Code size="1" variant="ghost">{b.id}</Code>
                            <Text size="1" color="gray">→</Text>
                            <Text size="1" weight="medium">{b.label}</Text>
                            {b.click_action && (
                              <Text size="1" color="gray">· tap: <Code size="1" variant="ghost">{b.click_action}</Code></Text>
                            )}
                          </Flex>
                        ))}
                      </Flex>
                    </Box>
                  )}
                </Flex>
              </Card>
              <Flex justify="end" mt="2">
                <Button variant="ghost" size="1" color="gray" onClick={() => setShowOverrides(true)}>
                  Customize this send
                </Button>
              </Flex>
            </Box>
          </>
        )}

        {(!usingTemplate || !selectedTemplate || showOverrides) && (
          <>
            <Separator size="4" />
            <Box>
              <SectionHeader
                title={usingTemplate ? "Overrides" : "Content"}
                subtitle={usingTemplate ? "Change any field for this one send only. Leave empty to inherit the template's value." : undefined}
              />
              <Flex direction="column" gap="3">
                <Box>
                  <Text as="label" size="2" weight="medium" mb="1" style={{ display: "block" }}>
                    {usingTemplate ? `Title (template: "${selectedTemplate?.title ?? ""}")` : "Title"}
                  </Text>
                  <TextField.Root value={title} onChange={(e) => setTitle(e.target.value)} placeholder={usingTemplate ? selectedTemplate?.title : undefined} />
                </Box>
                <Box>
                  <Text as="label" size="2" weight="medium" mb="1" style={{ display: "block" }}>
                    {usingTemplate ? "Body" : "Body"}
                  </Text>
                  <TextField.Root value={body} onChange={(e) => setBody(e.target.value)} placeholder={usingTemplate ? selectedTemplate?.body : undefined} />
                </Box>
                <ImagePickerField
                  value={imageUrl ?? ""}
                  onChange={(v) => setImageUrl(v ?? "")}
                  label={usingTemplate ? "Image" : "Image (optional)"}
                  allowManualUrl={false}
                  description="Android renders natively. iOS requires a Notification Service Extension."
                />
              </Flex>
            </Box>

            <Separator size="4" />

            <Box>
              <SectionHeader title="On tap" subtitle="Where the notification routes when tapped, plus optional action buttons on the banner." />
              <Flex direction="column" gap="3">
                <Box>
                  <Text as="label" size="2" weight="medium" mb="1" style={{ display: "block" }}>Deep link when the notification is tapped (optional)</Text>
                  <TextField.Root
                    value={clickAction}
                    onChange={(e) => setClickAction(e.target.value)}
                    placeholder={usingTemplate ? (selectedTemplate?.click_action ?? "/profile/settings") : "/profile/settings"}
                  />
                </Box>
                <IosButtonGroupPicker
                  value={category}
                  onChange={setCategory}
                  buttons={buttons}
                  onAutofillButtons={setButtons}
                />
                <PushButtonsField
                  value={buttons}
                  onChange={setButtons}
                  lockedByGroup={lockedByGroupSendTab}
                />
              </Flex>
              {usingTemplate && (
                <Flex justify="end" mt="3">
                  <Button variant="ghost" size="1" color="gray" onClick={() => {
                    setShowOverrides(false);
                    setTitle(""); setBody(""); setImageUrl("");
                    setClickAction(""); setCategory(""); setButtons([]);
                  }}>
                    Discard overrides
                  </Button>
                </Flex>
              )}
            </Box>
          </>
        )}

        <Separator size="4" />

        {/* ── 3. RECIPIENTS ────────────────────────────────────────────── */}
        <Box>
          <SectionHeader title="Recipients" subtitle="Pick users with live devices, or broadcast." />
          <RadioGroup.Root
            value={mode}
            onValueChange={(v) => setMode(v as "users" | "all")}
            mb="2"
          >
            <Flex gap="3">
              <RadioGroup.Item value="users">
                <Text size="2">Specific users</Text>
              </RadioGroup.Item>
              <RadioGroup.Item value="all">
                <Text size="2">Broadcast to all live devices</Text>
              </RadioGroup.Item>
            </Flex>
          </RadioGroup.Root>

        {mode === "users" && (
          <Box
            style={{
              border: "1px solid var(--gray-5)",
              borderRadius: "var(--radius-2)",
            }}
          >
            {selectedUsers.length > 0 && (
              <Box p="2" style={{ borderBottom: "1px solid var(--gray-5)", background: "var(--gray-a2)" }}>
                <Flex justify="between" align="center" mb="1">
                  <Text size="1" color="gray">Selected ({selectedUsers.length})</Text>
                  <Button
                    type="button"
                    variant="ghost"
                    size="1"
                    color="gray"
                    onClick={() => setSelectedIds(new Set())}
                  >
                    Clear all
                  </Button>
                </Flex>
                <Flex gap="1" wrap="wrap">
                  {selectedUsers.map((u) => (
                    <Badge key={u.id} variant="soft" size="1">
                      {u.email}
                      <IconButton
                        size="1"
                        variant="ghost"
                        color="gray"
                        ml="1"
                        onClick={() => toggle(u.id)}
                        aria-label={`Remove ${u.email}`}
                      >
                        ×
                      </IconButton>
                    </Badge>
                  ))}
                </Flex>
              </Box>
            )}
            <Box p="2" style={{ borderBottom: "1px solid var(--gray-5)" }}>
              <Flex gap="2" align="center">
                <Box style={{ flex: 1 }}>
                  <TextField.Root
                    placeholder={usersLoading ? "Loading…" : "Search by name or email…"}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    disabled={usersLoading}
                  />
                </Box>
                {filtered.length > 0 && filtered.length <= 50 && (
                  <Button
                    type="button"
                    variant="soft"
                    onClick={() => {
                      const allFilteredSelected = filtered.every((u) => selectedIds.has(u.id));
                      setSelectedIds((prev) => {
                        const next = new Set(prev);
                        if (allFilteredSelected) {
                          filtered.forEach((u) => next.delete(u.id));
                        } else {
                          filtered.forEach((u) => next.add(u.id));
                        }
                        return next;
                      });
                    }}
                  >
                    {filtered.every((u) => selectedIds.has(u.id)) ? "Deselect" : "Select"}{" "}
                    {filtered.length === users.length ? "all" : `${filtered.length} shown`}
                  </Button>
                )}
              </Flex>
            </Box>
            {usersLoading ? (
              <Box p="3">
                <Text size="2" color="gray">Loading registered users…</Text>
              </Box>
            ) : filtered.length === 0 ? (
              <Box p="3">
                <Text size="2" color="gray">
                  {users.length === 0
                    ? "No users have registered devices yet."
                    : "No users match that search."}
                </Text>
              </Box>
            ) : (
              <Box style={{ maxHeight: 320, overflowY: "auto" }}>
                {filtered.map((u) => {
                  const isSelected = selectedIds.has(u.id);
                  const providerBits = Object.entries(u.by_provider)
                    .map(([p, n]) => `${prettyProvider(p)}: ${n}`)
                    .join(" · ");
                  return (
                    <Flex
                      key={u.id}
                      align="center"
                      gap="3"
                      onClick={() => toggle(u.id)}
                      style={{
                        padding: "8px 12px",
                        borderBottom: "1px solid var(--gray-4)",
                        cursor: "pointer",
                        backgroundColor: isSelected ? "var(--accent-3)" : undefined,
                        userSelect: "none",
                      }}
                    >
                      <Box style={{ pointerEvents: "none" }}>
                        <Checkbox checked={isSelected} />
                      </Box>
                      <Flex direction="column" style={{ flex: 1 }}>
                        <Text size="2" weight={isSelected ? "medium" : "regular"}>
                          {u.first_name} {u.last_name}
                        </Text>
                        <Text size="1" color="gray">{u.email}</Text>
                      </Flex>
                      <Text size="1" color="gray">{providerBits}</Text>
                    </Flex>
                  );
                })}
              </Box>
            )}
          </Box>
        )}

        </Box>

        <Separator size="4" />
        <Box>
          <Button onClick={() => sendMutation.mutate()} disabled={sendDisabled} size="3">
            {sendMutation.isPending
              ? "Sending…"
              : mode === "all"
                ? "Send to all live devices"
                : selectedIds.size === 0
                  ? "Pick at least one recipient"
                  : `Send to ${selectedIds.size === 1 ? "1 user" : `${selectedIds.size} users`}`}
          </Button>
        </Box>
      </Flex>
      {editingTemplate && (
        <PushTemplateEditorDialog
          initial={editingTemplate}
          presetInitial={null}
          onClose={() => setEditingTemplate(null)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ["push-templates"] });
            setEditingTemplate(null);
          }}
        />
      )}
    </Card>
  );
}

function HistoryTab() {
  return <PushSendHistory />;
}

function SectionHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <Box mb="3">
      <Heading size="3" weight="medium">{title}</Heading>
      {subtitle && (
        <Text size="1" color="gray" mt="1" style={{ display: "block" }}>{subtitle}</Text>
      )}
    </Box>
  );
}

function prettyProvider(p: string): string {
  switch (p) {
    case "fcm": return "FCM";
    case "web_push": return "Web";
    case "apns": return "APNs";
    default: return p;
  }
}

